import React, { useMemo } from "react";
import { Timeline } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import type { TimelineItemProps } from "antd";
import styles from "./index.module.less";

// ==================== 类型定义 ====================
interface ProcessTimelineItem {
  approver: string;
  flowStatus: number | string;
  postName: string;
  createTime: string;
  message?: string;
  avatarUrl?: string;
}

interface ProcessTimelineProps {
  items: ProcessTimelineItem[];
  currentUserIndex?: number;
}

// ==================== 常量配置 ====================
const STATUS_CONFIG = {
  draft: {
    icon: "",
    color: "#BABABA",
    dot: "#BABABA",
    label: "草稿",
    tagColor: "default",
  },
  completed: {
    icon: <CheckOutlined />,
    color: "#00B42A",
    dot: "#00B42A",
    label: "审核通过",
    tagColor: "success",
  },
  pending: {
    icon: "",
    color: "#F47600",
    dot: "#BABABA",
    label: "审核中",
    tagColor: "warning",
  },
  rejected: {
    icon: <CloseOutlined />,
    color: "#E80000",
    dot: "#E80000",
    label: "已驳回",
    tagColor: "error",
  },
} as const;

// ==================== 辅助函数 ====================
/**
 * 根据 flowStatus 转换为状态类型（支持数字和字符串）
 * 草稿：[0, "0"]
 * 审核通过：[2, 8, "2", "8"]
 * 审核中：[1, "1"]
 * 审核驳回：其他
 */
const getStatusByFlowStatus = (
  flowStatus: number | string,
): "draft" | "completed" | "pending" | "rejected" => {
  // 将字符串转换为数字进行比较
  const statusNum =
    typeof flowStatus === "string" ? parseInt(flowStatus, 10) : flowStatus;

  if (statusNum === 0) {
    return "draft";
  }
  if ([2, 8].includes(statusNum)) {
    return "completed";
  }
  if (statusNum === 1) {
    return "pending";
  }
  return "rejected";
};

// ==================== 渲染函数 ====================
const renderStatus = (
  status: "draft" | "completed" | "pending" | "rejected",
) => {
  const config = STATUS_CONFIG[status];
  return (
    <div style={{ marginRight: 0, color: config.color }}>{config.label}</div>
  );
};

const renderProcessItem = (item: ProcessTimelineItem) => {
  const status = getStatusByFlowStatus(item.flowStatus);
  return (
    <div className={styles.processItemContainer}>
      <div className={styles.processItemContent}>
        <div className={styles.processItemHeader}>
          <img
            src={item.avatarUrl}
            alt=""
            className={styles.processItemAvatar}
          />
          <div className={styles.processItemInfo}>
            <div className={styles.processItemName}>{item.approver}</div>
            <div className={styles.processItemRole}>{item.postName}</div>
            <div className={styles.processItemTime}>{item.createTime}</div>
          </div>
        </div>
        {item.message && (
          <div className={styles.processItemComment}>{item.message}</div>
        )}
      </div>

      <div className={styles.processItemStatus}>{renderStatus(status)}</div>
    </div>
  );
};

// ==================== 主组件 ====================
const ProcessTimeline: React.FC<ProcessTimelineProps> = ({
  items,
  currentUserIndex,
}) => {
  const timelineItems = useMemo((): TimelineItemProps[] => {
    return items.map((item, index) => {
      const status = getStatusByFlowStatus(item.flowStatus);
      const config = STATUS_CONFIG[status];
      const isCurrent = index === currentUserIndex;

      return {
        dot: (
          <div
            className={`${styles.timelineDot} ${isCurrent ? styles.currentDot : ""}`}
            style={{
              backgroundColor: config.dot,
            }}
          >
            {config.icon}
          </div>
        ),
        children: renderProcessItem(item),
        color: config.color,
      };
    });
  }, [items, currentUserIndex]);

  return (
    <div className={styles.processTimelineContainer}>
      <Timeline items={timelineItems} />
    </div>
  );
};

export type { ProcessTimelineItem, ProcessTimelineProps };
export default ProcessTimeline;
